import React, { useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { Photo } from './photoType';
import Spinner from '../components/ui/spinner';

interface PhotoModalProps {
  photos: Photo[];
  currentPhotoIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onDeleteClick?: (photoId: number) => void;
}

const PhotoModal: React.FC<PhotoModalProps> = ({
  photos,
  currentPhotoIndex,
  isOpen,
  onClose,
  onNext,
  onPrevious,
  onDeleteClick,
}) => {
  const [isImageLoading, setIsImageLoading] = useState(true);

  // Reset loading state when photo changes or modal opens
  useEffect(() => {
    if (isOpen && photos[currentPhotoIndex]) {
      setIsImageLoading(true);
    }
  }, [currentPhotoIndex, isOpen, photos]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const hasNext = currentPhotoIndex < photos.length - 1;
      const hasPrevious = currentPhotoIndex > 0;

      if (e.key === 'ArrowRight' && hasNext) onNext();
      if (e.key === 'ArrowLeft' && hasPrevious) onPrevious();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPhotoIndex, photos.length, isOpen, onNext, onPrevious, onClose]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (currentPhotoIndex < photos.length - 1) onNext();
    },
    onSwipedRight: () => {
      if (currentPhotoIndex > 0) onPrevious();
    },
    trackMouse: false
  });

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleImageError = () => {
    setIsImageLoading(false);
  };

  if (!isOpen || !photos[currentPhotoIndex]) return null;

  const currentPhoto = photos[currentPhotoIndex];
  const hasNext = currentPhotoIndex < photos.length - 1;
  const hasPrevious = currentPhotoIndex > 0;

  return (
    <div 
      {...swipeHandlers}
      className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-[9999]"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 sm:top-5 sm:right-5 text-white hover:text-gray-300 transition-colors z-[10000] p-2"
        aria-label="Close"
      >
        <X className="size-7 sm:size-8" />
      </button>

      {/* Main image container */}
      <div 
        className="relative w-full sm:w-[90%] h-[70vh] sm:h-[80vh] flex items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading Spinner */}
        {isImageLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-[9999]">
            <Spinner size="lg" fullScreen={false} className="border-white border-t-primary" />
          </div>
        )}

        {/* Previous button */}
        {hasPrevious && (
          <button
            onClick={onPrevious}
            className="absolute left-2 sm:left-5 bg-white/20 hover:bg-white/30 transition-colors rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white z-[10000]"
            aria-label="Previous photo"
          >
            <ChevronLeft className="size-6 sm:size-7" />
          </button>
        )}

        {/* Main image */}
        <img
          src={currentPhoto.url}
          alt={currentPhoto.file_name}
          className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${
            isImageLoading ? 'opacity-0' : 'opacity-100'
          }`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />

        {/* Next button */}
        {hasNext && (
          <button
            onClick={onNext}
            className="absolute right-2 sm:right-5 bg-white/20 hover:bg-white/30 transition-colors rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white z-[10000]"
            aria-label="Next photo"
          >
            <ChevronRight className="size-6 sm:size-7" />
          </button>
        )}

        {/* Delete button */}
        {onDeleteClick && (
          <button
            onClick={() => onDeleteClick(currentPhoto.id)}
            className="absolute bottom-3 right-3 sm:bottom-5 sm:right-5 bg-red-600 hover:bg-red-700 transition-colors rounded-full w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center text-white shadow-lg z-[10000]"
            aria-label="Delete photo"
          >
            <Trash2 className="size-5" />
          </button>
        )}
      </div>

      {/* Photo counter */}
      <div className="text-white mt-4 sm:mt-5 text-sm sm:text-base font-medium z-[10000]">
        {currentPhotoIndex + 1} of {photos.length}
      </div>
    </div>
  );
};

export default PhotoModal;